import { DecimalPipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { ColumnMenuSettings, DataStateChangeEvent, GridDataResult, SelectableSettings } from '@progress/kendo-angular-grid';
import { CompositeFilterDescriptor, State, process } from "@progress/kendo-data-query";
import { Observable, of, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, take } from 'rxjs/operators';
import { ArraysUtil } from '../arrays-util/arrays-util';
import { GridPagerConfig } from '../grid-pager-config/grid-pager-config';
import { DestroyBase } from '../ng-destroy-base';
import { AppServiceHelperService } from '../../../services/app-service-helper.service';
import { EventBusService } from '../../../services/event-bus.service';
import { ObjectUtil } from '../object-util/object-util';

/**
 * Dialog for assigning properties
 */
@Component({
  selector: 'assign-properties-dialog',
  templateUrl: './assign-properties-dialog.component.html',
  styleUrls: ['./assign-properties-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AssignPropertiesDialogComponent extends DestroyBase implements OnInit {

  /**
   * Search term
   */
  searchTerm: any = "";

  /**
   * The search term as subject
   */
  private searchTerm$: Subject<string> = new Subject<string>();

  /**
   * Current page items (different than kendoGrid.data or kendoGrid.view - depends on grouping)
   */
  public currentPageItems: any[];

  /**
   * Indicator that showing the dialog
   */
  isDialogDisplayed = false;

  /**
   * Callback function
   */
  action: any;

  /**
   * Original data loaded for grid
   */
  private loadedData: any = null;

  /**
   * Pager config
   */
  public pagerConfig: GridPagerConfig = {
    pageSize: 10,
    sizes: [10, 20],
    numericButtonsCount: 0
  }

  /**
   * Grid GridDataResult
   */
  public gridDataResult: GridDataResult = { data: [], total: 0 };

  /**
   * Loading indicator
   */
  public loading = false;

  /**
   * Grid SelectableSettings
   */
  public selectableSettings: SelectableSettings = {
    enabled: true,
    mode: 'single'
  };
  
  /**
   * Pager info message (f.e. 1 - 50 OF 27.136 ITEMS)
   */
  protected get pagerInfoMessage(): string {
    const from: number = Math.min(this.state.skip + 1, this.gridDataResult.total);
    const to: number = Math.min(this.state.skip + this.state.take, this.gridDataResult.total);
    const itemsCount: number = this.gridDataResult.total;
    return `${this.decimalPipe.transform(from, '1.0-0')} - ${this.decimalPipe.transform(to, '1.0-0')} OF ${this.decimalPipe.transform(itemsCount, '1.0-0')} ITEMS`;
  }

  /**
   * Grid ColumnMenuSettings
   */
  public columnMenuSettings: ColumnMenuSettings = {
    filter: false
  };

  /**
   * The current grid state
   */
  public state: State = {
    skip: 0,
    take: this.pagerConfig.pageSize,
    group: [],
    filter: { filters: [], logic: "and" },
    sort: []
  };

  /**
   * Constructor
   * @param eventBus EventBusService
   * @param decimalPipe DecimalPipe
   * @param http HttpClient
   * @param appServiceHelper AppServiceHelperService
   */
  constructor(private eventBus: EventBusService,
    private decimalPipe: DecimalPipe,
    private http: HttpClient,
    private appServiceHelper: AppServiceHelperService) {
    super();
  }

  /**
   * On init
   */
  ngOnInit(): void {
    this.onEvents.push(this.eventBus.subscribeEvent('openAssignPropertyDialog', (config, _options) => {
      this.initDialog(config);
    }));

    this.subscriptions.add(this.searchTerm$
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((searchTerm: any) => {
        this.applySearchFilters(searchTerm);
      }));
  }

  /**
   * Resets the dialog
   */
  public resetDialog() {
    this.searchTerm$.next('');
    this.isDialogDisplayed = false;
    this.state = {
      skip: 0,
      take: this.pagerConfig.pageSize,
      group: [],
      filter: { filters: [], logic: "and" },
      sort: []
    }
    this.gridDataResult = { data: [], total: 0 }
  }

  /**
   * Initializes the dialog
   * @param config
   */
  public initDialog(config: any): void {
    this.resetDialog();
    this.action = config.action;
    this.isDialogDisplayed = true;
    this.loadData({
      take: this.pagerConfig.pageSize,
      skip: 0,
      filter: null,
      group: null,
      sort: null
    });
  }

  /**
   * On cancle action
   */
  cancel() {
    this.resetDialog();
    this.searchTerm = "";
  }

  /**
   * Checks if data loading should be triggered
   * @param gridDataState
   * @returns
   */
  private shouldTriggerLoad(gridDataState: DataStateChangeEvent): boolean {
    const sortChanged = !ArraysUtil.arraysEqual(this.state.sort, gridDataState.sort);
    const groupChanged = !ArraysUtil.arraysEqual(this.state.group, gridDataState.group);
    const skipChanged = this.state.skip !== gridDataState.skip;
    const takeChanged = this.state.take !== gridDataState.take;

    const triggerLoad = skipChanged || takeChanged || (!sortChanged && !groupChanged);
    return triggerLoad;
  }

  /**
   * The data state was changed
   * @param gridDataState The new data state
   * @returns 
   */
  public dataStateChanged(gridDataState: DataStateChangeEvent): Promise<any> {
    if (this.shouldTriggerLoad(gridDataState)) {
      return this.loadData(gridDataState);
    } else {
      this.setState(gridDataState);
      this.processGrid(this.loadedData, this.gridDataResult.total);
    }
  }

  /**
   * Process the grid
   * @param data Data
   * @param total Total data entries
   */
  private processGrid(data: any, total: number): void {
    const skip = this.state.skip;
    if (total > this.loadedData.length) {
      this.state.skip = 0;
    }

    const filter = this.state.filter;
    this.state.filter = null;

    this.gridDataResult = process(data || [], this.state);
    this.gridDataResult.total = total;

    //reapply skip and filter
    this.state.skip = skip;
    this.state.filter = filter;

    //Also calculate array of items on current page
    const flattenArrays = arr => arr.reduce((a, e) => [...a, ...e], []);
    //grouped grid has "group rows" with items array, so cover both cases
    const arrays = this.gridDataResult.data.map(item => Array.isArray(item.items) ? item.items : [item]);
    this.currentPageItems = flattenArrays(arrays);
  }

  /**
   * Sets the grid state
   * @param newState
   */
  private setState(newState: DataStateChangeEvent | State): void {
    this.state.filter = newState.filter;
    this.state.group = newState.group;
    this.state.skip = newState.skip;
    this.state.sort = newState.sort;
    this.state.take = newState.take;
  }

  /**
   * Gets the url for loading a paged list
   * If server paging is not supported return null and override getDataUrl
   * @returns The url for loading a paged list
   */
  protected getPagedListEndpoint(): string {
    return this.appServiceHelper.getServiceEndpoint('pollux.projectValuation.api', `Properties/list/paged?valEventId=0`);
  }

  /**
   * Builds the paged list endpoint URL
   * @param gridDataState The grid's state
   * @returns The URL to be called to get paged data
   */
  private buildPagedListEndpoint(gridDataState: State) {
    const pagedListUrl: string = this.getPagedListEndpoint();
    if (pagedListUrl) {
      const filterQueryString = this.getFilterQueryString(gridDataState.filter);
      const page = (gridDataState.skip / gridDataState.take) + 1;
      const queryStringFirstSeparator = pagedListUrl.indexOf('?') === -1 ? '?' : '&';//pagedListUrl could already contain ?
      const url = `${pagedListUrl}${queryStringFirstSeparator}skip=${gridDataState.skip}&take=${gridDataState.take}&page=${page}&pageSize=${gridDataState.take}${filterQueryString}`;
      return url;
    }

    return null;
  }

  /**
   * Gets a filter query string
   * @param filter Kedo grid filter
   * @returns The filter string to be sent to the API
   */
  private getFilterQueryString(filter: CompositeFilterDescriptor): string {
    //https://www.telerik.com/blogs/server-side-filtering-kendo-angular-ui-grid
    const hasCustomFilter: boolean = filter?.filters?.length > 0;
    let filterQueryString = '';
    if (hasCustomFilter) {
      const filterCopy = ObjectUtil.deepCopy(filter);
      filterCopy.useV2ServerFilter = true;
      filterQueryString = `&filter=${encodeURIComponent(JSON.stringify(filterCopy))}`;
    }

    return filterQueryString;
  }

  /**
   * Loads data
   * @param gridDataState
   * @param selectedItemOid The selected item OID. If not null, this item will be selected after loading if it appears on the loaded page
   * @returns A Promise to wait for
   */
  public loadData(gridDataState: State): Promise<any> {
    return new Promise((resolve, reject) => {
      this.loading = true;

      if (!gridDataState) {
        gridDataState = {};
      }

      if (!gridDataState.take) {
        gridDataState.take = this.pagerConfig.pageSize;
      }

      const url = this.buildPagedListEndpoint(gridDataState);
      //if no url provided emit empty result
      const data$: Observable<any> = url ? this.http.get(url) : of([]);
      data$.pipe(take(1)).subscribe({
        next: (data: any) => {
          this.loading = false;
          this.setState(gridDataState);

          if (data) {
            this.gridDataResult.data = data.details;
            this.gridDataResult.total = data.total;
          }
          else {
            this.gridDataResult.data = [];
            this.gridDataResult.total = 0;
          }

          //remember original loaded data for grids that doesn't use server side paging/filtering
          this.loadedData = this.gridDataResult.data;
          this.processGrid(this.loadedData, this.gridDataResult.total);
          resolve(data);
        }, error: (error: any) => {
          this.loading = false;
          reject(error);
        }
      });
    });
  }

  /**
   * Performs a search
   */
  public search() {
    this.searchTerm$.next(this.searchTerm);
  }

  /**
   * Search action
   * @param searchTerm
   */
  private applySearchFilters(searchTerm: string) {
    if (searchTerm.length) {
      const filters: CompositeFilterDescriptor = {
        logic: 'or',
        filters: [
          {
            field: 'Oid',
            operator: 'contains',
            value: searchTerm
          },
          {
            field: 'Street1',
            operator: 'contains',
            ignoreCase: true,
            value: searchTerm
          },
          {
            field: 'PropertyType',
            operator: 'contains',
            ignoreCase: true,
            value: searchTerm
          },
          {
            field: 'CountryIDDescription',
            operator: 'contains',
            ignoreCase: true,
            value: searchTerm
          },
          {
            field: 'PostalCodeManual',
            operator: 'contains',
            ignoreCase: true,
            value: searchTerm
          },
          {
            field: 'MunicipalManual',
            operator: 'contains',
            ignoreCase: true,
            value: searchTerm
          },
          {
            field: 'NoJll',
            operator: 'contains',
            ignoreCase: true,
            value: searchTerm
          },
          {
            field: 'NoNl',
            operator: 'contains',
            ignoreCase: true,
            value: searchTerm
          },
          {
            field: 'buildingName',
            operator: 'contains',
            ignoreCase: true,
            value: searchTerm
          },
          {
            field: 'ProjectStdID',
            operator: 'contains',
            value: searchTerm
          }
        ]
      };
      this.loadData({
        take: this.pagerConfig.pageSize,
        skip: 0,
        filter: filters,
        group: null,
        sort: null
      });
    } else {
      this.loadData({
        take: this.pagerConfig.pageSize,
        skip: 0,
        filter: null,
        group: null,
        sort: null
      });
    }
  }

  /**
   * On grid row selection
   * @param selectedEvent
   */
  onRowSelected(selectedEvent: any) {
    this.action(selectedEvent.selectedRows[0].dataItem);
    this.cancel();
  }

}
